import * as Yup from "yup";

export const IE_COMPLAINT_MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

export const IE_COMPLAINT_MESSAGES = {
  contactRequired: "Please enter Complainant Contact Number.",
  contactInvalid: "Contact Number should contain exactly 10 digits.",
  kpiRequired: "Please select KPI Category.",
  issueTypeRequired: "Please select Issue Type.",
  zoneRequired: "Please select Zone.",
  wardRequired: "Please select Ward.",
  colonyRequired: "Please select Colony Name.",
  locationRequired: "Please enter Complaint Location.",
  locationInvalid: "HTML, JavaScript and SQL scripts are not permitted in Location.",
  descriptionRequired: "Please enter Complaint Description.",
  descriptionMin: "Description should contain minimum 10 characters.",
  descriptionMax: "Description cannot exceed 1000 characters.",
  descriptionScript: "HTML, JavaScript and SQL scripts are not permitted.",
  latitudeRequired: "Latitude is mandatory.",
  longitudeRequired: "Longitude is mandatory.",
  invalidGps: "Invalid GPS coordinates detected.",
  actionRemarksRequired: "Action Remarks is required.",
  actionRemarksMin: "Action Remarks should contain minimum 10 characters.",
  actionRemarksMax: "Action Remarks cannot exceed 1000 characters.",
  actionRemarksScript: "HTML and script content are not permitted.",
  closingImageRequired: "Complaint cannot be closed without Closing Image.",
  invalidImageType: "Only PNG, JPEG and JPG files are allowed.",
  imageSizeExceeded: "Image file size exceeds the maximum allowed limit.",
  fromDateGreater: "From Date cannot be greater than To Date.",
  invalidDateRange: "Please select valid Date Range.",
  futureDateNotAllowed: "From Date and To Date cannot be a future date.",
  duplicateComplaint:
    "An open complaint with the same Contact No, Location, Zone, Ward and Colony already exists.",
};

const SCRIPT_PATTERN =
  /<script\b|javascript\s*:|on\w+\s*=|(\b(SELECT|INSERT|UPDATE|DELETE|DROP|UNION|ALTER|CREATE)\b)/i;

export const formatContactNoInput = (value = "") => {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (!digits) return "";
  if (!/^[6-9]/.test(digits)) return "";
  return digits;
};

export const formatLocationInput = (value = "") => value.slice(0, 250);

export const containsScriptContent = (value = "") => SCRIPT_PATTERN.test(value);

export const isValidLocationValue = (value = "") => {
  const trimmed = value.trim();
  if (!trimmed) return false;
  return !containsScriptContent(trimmed);
};

export const isSequentialDigits = (value = "") => {
  const digits = value.split("").map(Number);
  if (digits.length !== 10) return false;

  let ascending = true;
  let descending = true;
  for (let index = 1; index < digits.length; index += 1) {
    if (digits[index] !== digits[index - 1] + 1) ascending = false;
    if (digits[index] !== digits[index - 1] - 1) descending = false;
  }
  return ascending || descending;
};

export const isInvalidMobileNumber = (value = "") => {
  if (!value) return true;
  if (!/^[6-9]\d{9}$/.test(value)) return true;
  if (/^(\d)\1{9}$/.test(value)) return true;
  if (isSequentialDigits(value)) return true;
  return false;
};

export const parseCoordinate = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

export const isValidLatitude = (value) => {
  const parsed = parseCoordinate(value);
  return parsed !== null && parsed >= -90 && parsed <= 90;
};

export const isValidLongitude = (value) => {
  const parsed = parseCoordinate(value);
  return parsed !== null && parsed >= -180 && parsed <= 180;
};

export const isValidFilterDateRange = (fromDate, toDate) => {
  if (!fromDate || !toDate) return true;
  return fromDate <= toDate;
};

export const isFutureFilterDate = (fromDate, toDate) => {
  const today = new Date().toISOString().split("T")[0];
  return (fromDate && fromDate > today) || (toDate && toDate > today);
};

const descriptionValidation = ({ enforceMinLength = true } = {}) => {
  let schema = Yup.string()
    .trim()
    .required(IE_COMPLAINT_MESSAGES.descriptionRequired)
    .test("not-blank", IE_COMPLAINT_MESSAGES.descriptionRequired, (value) =>
      Boolean(value?.trim())
    );

  if (enforceMinLength) {
    schema = schema.min(10, IE_COMPLAINT_MESSAGES.descriptionMin);
  }

  return schema
    .max(1000, IE_COMPLAINT_MESSAGES.descriptionMax)
    .test(
      "no-script",
      IE_COMPLAINT_MESSAGES.descriptionScript,
      (value) => !containsScriptContent(value || "")
    );
};

const optionalUploadFileValidation = () =>
  Yup.mixed()
    .nullable()
    .notRequired()
    .test("fileType", IE_COMPLAINT_MESSAGES.invalidImageType, (value) => {
      if (!value) return true;
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
      return (
        allowedTypes.includes(value.type) ||
        /\.(jpe?g|png)$/i.test(value.name || "")
      );
    })
    .test("fileSize", IE_COMPLAINT_MESSAGES.imageSizeExceeded, (value) => {
      if (!value) return true;
      return value.size <= IE_COMPLAINT_MAX_IMAGE_SIZE_BYTES;
    });

export const buildIeComplaintValidationSchema = ({
  isEditMode = false,
  statusOptions = ["Closed", "Invalid"],
} = {}) => {
  if (isEditMode) {
    return Yup.object().shape({
      status: Yup.string()
        .required("Status is required")
        .oneOf(
          statusOptions.length > 0 ? statusOptions : ["Closed", "Invalid"],
          "Status is required"
        ),
      description: descriptionValidation({ enforceMinLength: false }),
      actionRemarks: Yup.string()
        .trim()
        .required(IE_COMPLAINT_MESSAGES.actionRemarksRequired)
        .min(10, IE_COMPLAINT_MESSAGES.actionRemarksMin)
        .max(1000, IE_COMPLAINT_MESSAGES.actionRemarksMax)
        .test(
          "no-script",
          IE_COMPLAINT_MESSAGES.actionRemarksScript,
          (value) => !containsScriptContent(value || "")
        ),
      uploadFile: Yup.mixed().nullable().notRequired(),
    });
  }

  return Yup.object().shape({
    contactNo: Yup.string()
      .required(IE_COMPLAINT_MESSAGES.contactRequired)
      .test("contact-format", IE_COMPLAINT_MESSAGES.contactInvalid, (value) => {
        if (!value) return false;
        return !isInvalidMobileNumber(value);
      }),
    kpiCategory: Yup.string().required(IE_COMPLAINT_MESSAGES.kpiRequired),
    issueType: Yup.string().required(IE_COMPLAINT_MESSAGES.issueTypeRequired),
    zoneId: Yup.string().required(IE_COMPLAINT_MESSAGES.zoneRequired),
    wardId: Yup.string().required(IE_COMPLAINT_MESSAGES.wardRequired),
    colonyName: Yup.string().required(IE_COMPLAINT_MESSAGES.colonyRequired),
    location: Yup.string()
      .trim()
      .required(IE_COMPLAINT_MESSAGES.locationRequired)
      .max(250, IE_COMPLAINT_MESSAGES.locationInvalid)
      .test("location-format", IE_COMPLAINT_MESSAGES.locationInvalid, (value) =>
        isValidLocationValue(value || "")
      ),
    description: descriptionValidation(),
    latitude: Yup.string()
      .required(IE_COMPLAINT_MESSAGES.latitudeRequired)
      .test("latitude-range", IE_COMPLAINT_MESSAGES.invalidGps, (value) =>
        isValidLatitude(value)
      ),
    longitude: Yup.string()
      .required(IE_COMPLAINT_MESSAGES.longitudeRequired)
      .test("longitude-range", IE_COMPLAINT_MESSAGES.invalidGps, (value) =>
        isValidLongitude(value)
      ),
    complaintCreatedOn: Yup.string()
      .required("Complaint Created On is required")
      .test("not-future", "Complaint Created On cannot be a future date", (value) => {
        if (!value) return true;
        const today = new Date().toISOString().split("T")[0];
        return value <= today;
      }),
    actionRemarks: Yup.string().notRequired(),
    uploadFile: optionalUploadFileValidation(),
  });
};